/**
 * Flue-shaped persistence for Node self-host.
 *
 * Cloudflare keeps Durable Object SQLite and rejects this module as a runtime
 * (DocumentAgent + R2). Node brings a SQL runner — Postgres in production,
 * in-memory in tests — and stores PDF bytes in SQL until agents need buckets.
 *
 * Spec: internal/specs/self-host-node-postgres.md
 */

import { Effect, Schema } from 'effect';

export type SqlParams = readonly unknown[];

export type SqlTx = {
  query<T = Record<string, unknown>>(text: string, params?: SqlParams): Promise<T[]>;
};

/**
 * Bring-your-own-driver contract, matching `@flue/postgres`:
 * `query` / `transaction` (one connection) / `close`.
 */
export type SqlRunner = {
  query<T = Record<string, unknown>>(text: string, params?: SqlParams): Promise<T[]>;
  transaction<T>(fn: (tx: SqlTx) => Promise<T>): Promise<T>;
  close(): Promise<void>;
};

export type SqlRunnerOperation =
  | 'query'
  | 'connect'
  | 'begin'
  | 'transaction-query'
  | 'transaction'
  | 'commit'
  | 'rollback'
  | 'release'
  | 'close';

export class SqlRunnerError extends Schema.TaggedError<SqlRunnerError>()('SqlRunnerError', {
  operation: Schema.Literals([
    'query',
    'connect',
    'begin',
    'transaction-query',
    'transaction',
    'commit',
    'rollback',
    'release',
    'close',
  ]),
  message: Schema.String,
  cause: Schema.Defect(),
}) {}

export interface EffectSqlTx<E = SqlRunnerError> {
  readonly queryEffect: <T = Record<string, unknown>>(
    text: string,
    params?: SqlParams,
  ) => Effect.Effect<T[], E>;
}

export interface EffectSqlRunner<E = SqlRunnerError> extends EffectSqlTx<E> {
  readonly transactionEffect: <A, E2, R>(
    use: (transaction: EffectSqlTx<E>) => Effect.Effect<A, E2, R>,
  ) => Effect.Effect<A, E | E2, R>;
  readonly closeEffect: () => Effect.Effect<void, E>;
}

export type DefinedEffectSqlRunner<E = SqlRunnerError> = SqlRunner & EffectSqlRunner<E>;

export function makeSqlRunnerError(operation: SqlRunnerOperation, cause: unknown): SqlRunnerError {
  return new SqlRunnerError({
    operation,
    message: cause instanceof Error ? cause.message : String(cause),
    cause,
  });
}

export function isEffectSqlRunner(runner: SqlRunner): runner is DefinedEffectSqlRunner {
  return (
    typeof (runner as Partial<EffectSqlRunner>).queryEffect === 'function' &&
    typeof (runner as Partial<EffectSqlRunner>).transactionEffect === 'function' &&
    typeof (runner as Partial<EffectSqlRunner>).closeEffect === 'function'
  );
}

/** Derive the Flue-shaped Promise SQL runner from one native implementation. */
export function defineEffectSqlRunner<E extends { readonly cause: unknown }>(
  definition: EffectSqlRunner<E>,
): DefinedEffectSqlRunner<E> {
  return {
    ...definition,
    query: <T = Record<string, unknown>>(text: string, params?: SqlParams) =>
      runPromiseRestoringCause(definition.queryEffect<T>(text, params)),
    transaction: <T>(use: (transaction: SqlTx) => Promise<T>) =>
      runPromiseRestoringCause(
        definition.transactionEffect((transaction) =>
          Effect.tryPromise({
            try: () =>
              use({
                query: <R = Record<string, unknown>>(text: string, params?: SqlParams) =>
                  runPromiseRestoringCause(transaction.queryEffect<R>(text, params)),
              }),
            catch: (cause) => makeSqlRunnerError('transaction', cause),
          }),
        ),
      ),
    close: () => runPromiseRestoringCause(definition.closeEffect()),
  };
}

export type PersistenceClock = {
  now(): number;
};

export type PersistenceOptions = {
  clock?: PersistenceClock;
};

export type DocumentRecord = {
  id: string;
  ownerUserId: string;
  orgId: string | null;
  projectId: string | null;
  isPublished: boolean;
  spec: Record<string, unknown>;
  meta: Record<string, unknown>;
  graph: Record<string, unknown>;
  phase: string;
  createdAt: string;
  updatedAt: string;
};

export type BlobKind = 'source' | 'media';

export type BlobRecord = {
  documentId: string;
  kind: BlobKind;
  name: string;
  contentType: string;
  bytes: Uint8Array;
};

export type LeaseRecord = {
  documentId: string;
  instanceId: string;
  expiresAt: string;
};

export type DocumentWrite = Omit<DocumentRecord, 'createdAt' | 'updatedAt'> & {
  createdAt?: string;
  updatedAt?: string;
};

export type DocumentPersistence = {
  migrate(): Promise<void>;
  close(): Promise<void>;
  putDocument(doc: DocumentWrite): Promise<DocumentRecord>;
  getDocument(id: string): Promise<DocumentRecord | null>;
  listDocumentsByOwner(ownerUserId: string): Promise<DocumentRecord[]>;
  putBlob(blob: BlobRecord): Promise<void>;
  getBlob(documentId: string, kind: BlobKind, name: string): Promise<BlobRecord | null>;
  /**
   * One live owner per document (Flue). Returns true when this instance owns
   * the document for `ttlMs`. False when another unexpired instance holds it.
   */
  acquireLease(documentId: string, instanceId: string, ttlMs: number): Promise<boolean>;
  releaseLease(documentId: string, instanceId: string): Promise<void>;
  getLease(documentId: string): Promise<LeaseRecord | null>;
};

export type DocumentPersistenceOperation =
  | 'migrate'
  | 'close'
  | 'put-document'
  | 'get-document'
  | 'list-documents-by-owner'
  | 'put-blob'
  | 'get-blob'
  | 'acquire-lease'
  | 'release-lease'
  | 'get-lease';

/** Typed failure for the reusable Node self-host persistence boundary. */
export class DocumentPersistenceError extends Schema.TaggedError<DocumentPersistenceError>()(
  'DocumentPersistenceError',
  {
    operation: Schema.Literals([
      'migrate',
      'close',
      'put-document',
      'get-document',
      'list-documents-by-owner',
      'put-blob',
      'get-blob',
      'acquire-lease',
      'release-lease',
      'get-lease',
    ]),
    message: Schema.String,
    cause: Schema.Defect(),
  },
) {}

export interface EffectDocumentPersistence<E = DocumentPersistenceError> {
  readonly migrateEffect: () => Effect.Effect<void, E>;
  readonly closeEffect: () => Effect.Effect<void, E>;
  readonly putDocumentEffect: (document: DocumentWrite) => Effect.Effect<DocumentRecord, E>;
  readonly getDocumentEffect: (id: string) => Effect.Effect<DocumentRecord | null, E>;
  readonly listDocumentsByOwnerEffect: (ownerUserId: string) => Effect.Effect<DocumentRecord[], E>;
  readonly putBlobEffect: (blob: BlobRecord) => Effect.Effect<void, E>;
  readonly getBlobEffect: (
    documentId: string,
    kind: BlobKind,
    name: string,
  ) => Effect.Effect<BlobRecord | null, E>;
  readonly acquireLeaseEffect: (
    documentId: string,
    instanceId: string,
    ttlMs: number,
  ) => Effect.Effect<boolean, E>;
  readonly releaseLeaseEffect: (documentId: string, instanceId: string) => Effect.Effect<void, E>;
  readonly getLeaseEffect: (documentId: string) => Effect.Effect<LeaseRecord | null, E>;
}

export type DefinedEffectDocumentPersistence<E = DocumentPersistenceError> = DocumentPersistence &
  EffectDocumentPersistence<E>;

export function makeDocumentPersistenceError(
  operation: DocumentPersistenceOperation,
  cause: unknown,
): DocumentPersistenceError {
  return new DocumentPersistenceError({
    operation,
    message: cause instanceof Error ? cause.message : String(cause),
    cause,
  });
}

function runPromiseRestoringCause<A, E extends { readonly cause: unknown }>(
  effect: Effect.Effect<A, E>,
): Promise<A> {
  return Effect.runPromise(effect.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

/**
 * Derive the stable Promise persistence contract from one native Effect
 * implementation so Node self-host callers can migrate incrementally.
 */
export function defineEffectDocumentPersistence<E extends { readonly cause: unknown }>(
  definition: EffectDocumentPersistence<E>,
): DefinedEffectDocumentPersistence<E> {
  return {
    ...definition,
    migrate: () => runPromiseRestoringCause(definition.migrateEffect()),
    close: () => runPromiseRestoringCause(definition.closeEffect()),
    putDocument: (document) => runPromiseRestoringCause(definition.putDocumentEffect(document)),
    getDocument: (id) => runPromiseRestoringCause(definition.getDocumentEffect(id)),
    listDocumentsByOwner: (ownerUserId) =>
      runPromiseRestoringCause(definition.listDocumentsByOwnerEffect(ownerUserId)),
    putBlob: (blob) => runPromiseRestoringCause(definition.putBlobEffect(blob)),
    getBlob: (documentId, kind, name) =>
      runPromiseRestoringCause(definition.getBlobEffect(documentId, kind, name)),
    acquireLease: (documentId, instanceId, ttlMs) =>
      runPromiseRestoringCause(definition.acquireLeaseEffect(documentId, instanceId, ttlMs)),
    releaseLease: (documentId, instanceId) =>
      runPromiseRestoringCause(definition.releaseLeaseEffect(documentId, instanceId)),
    getLease: (documentId) => runPromiseRestoringCause(definition.getLeaseEffect(documentId)),
  };
}

export function isEffectDocumentPersistence(
  persistence: DocumentPersistence,
): persistence is DefinedEffectDocumentPersistence {
  const candidate = persistence as Partial<EffectDocumentPersistence>;
  return (
    typeof candidate.migrateEffect === 'function' &&
    typeof candidate.closeEffect === 'function' &&
    typeof candidate.putDocumentEffect === 'function' &&
    typeof candidate.getDocumentEffect === 'function' &&
    typeof candidate.listDocumentsByOwnerEffect === 'function' &&
    typeof candidate.putBlobEffect === 'function' &&
    typeof candidate.getBlobEffect === 'function' &&
    typeof candidate.acquireLeaseEffect === 'function' &&
    typeof candidate.releaseLeaseEffect === 'function' &&
    typeof candidate.getLeaseEffect === 'function'
  );
}

function liftDocumentPersistence<A>(
  operation: DocumentPersistenceOperation,
  evaluate: () => Promise<A>,
): Effect.Effect<A, DocumentPersistenceError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => makeDocumentPersistenceError(operation, cause),
  });
}

/** Lift a custom Promise-only persistence adapter for native consumers. */
export function effectDocumentPersistenceFromPromise(
  persistence: DocumentPersistence,
): EffectDocumentPersistence {
  if (isEffectDocumentPersistence(persistence)) return persistence;
  return {
    migrateEffect: () => liftDocumentPersistence('migrate', () => persistence.migrate()),
    closeEffect: () => liftDocumentPersistence('close', () => persistence.close()),
    putDocumentEffect: (document) =>
      liftDocumentPersistence('put-document', () => persistence.putDocument(document)),
    getDocumentEffect: (id) =>
      liftDocumentPersistence('get-document', () => persistence.getDocument(id)),
    listDocumentsByOwnerEffect: (ownerUserId) =>
      liftDocumentPersistence('list-documents-by-owner', () =>
        persistence.listDocumentsByOwner(ownerUserId),
      ),
    putBlobEffect: (blob) => liftDocumentPersistence('put-blob', () => persistence.putBlob(blob)),
    getBlobEffect: (documentId, kind, name) =>
      liftDocumentPersistence('get-blob', () => persistence.getBlob(documentId, kind, name)),
    acquireLeaseEffect: (documentId, instanceId, ttlMs) =>
      liftDocumentPersistence('acquire-lease', () =>
        persistence.acquireLease(documentId, instanceId, ttlMs),
      ),
    releaseLeaseEffect: (documentId, instanceId) =>
      liftDocumentPersistence('release-lease', () =>
        persistence.releaseLease(documentId, instanceId),
      ),
    getLeaseEffect: (documentId) =>
      liftDocumentPersistence('get-lease', () => persistence.getLease(documentId)),
  };
}
